<script setup lang="ts">
import { ref, watch } from 'vue';
import PermissionLock from '../PermissionLock.vue';
import type { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { checkEmployeeOrAdminPermission } from '@ilteducation/customer-mgmt-permissions';

import useToast from '../../composables/useToast';
import { useLocale } from '../../composables/useLocale';
import useModal, { modals } from '../../composables/useModal';
import { usePermissions } from '../../composables/usePermissions';
import { useSidebarGroup } from '../../composables/useSidebarGroup';

const { t } = useLocale();

const props = defineProps<{
  group: CustomerMgmtTypes.GetGroupResponse;
}>();

const { open: openSetPassword } = useModal(modals.SET_GROUP_PASSWORD);
const { close: closeEditGroupLoginModal } = useModal(modals.EDIT_GROUP_LOGIN);
const { members, isMembersLoading } = useSidebarGroup();
const { toast } = useToast();
const { permissionContext, reporterWithReason, getCallerAssociation } = usePermissions();

const isGroupStudentPasswordEditableCheck = ref<ReturnType<typeof reporterWithReason>>({
  allowed: false,
  reason: 'not_permitted',
});
watch(
  [props.group],
  () => {
    checkEmployeeOrAdminPermission(
      permissionContext.value,
      {
        organization: props.group.schoolUnit,
      },
      getCallerAssociation,
      reporterWithReason,
    ).then((res) => {
      isGroupStudentPasswordEditableCheck.value = res;
    });
  },
  { immediate: true },
);

const openSetPasswordModal = async () => {
  // full members are fetched at the moment but still don't fully rely on sidebar groups for changing the passwords.
  if (!members.value || members.value.length === 0) {
    // No members to set password for
    toast(t('lcp.group_has_no_members'), 'warning');
    return;
  }

  closeEditGroupLoginModal();
  openSetPassword();
};
</script>

<template>
  <v-row no-gutters>
    <v-col cols="12" class="d-flex ga-2 align-center pb-4">
      <span class="font-weight-bold">{{ t('lcp.set_password_to_group') }}</span>
    </v-col>
    <v-col cols="12" class="d-flex ga-2 align-center pb-4">
      <p>
        {{ t('lcp.set_password_to_group_info') }}
      </p>
    </v-col>
    <v-col cols="12" class="d-flex ga-2">
      <v-row>
        <v-col cols="auto">
          <v-chip
            @click="openSetPasswordModal"
            :disabled="isMembersLoading || !isGroupStudentPasswordEditableCheck.allowed"
            class="mx-0"
          >
            {{ t('lcp.new_password') }}
            <template #prepend>
              <v-icon class="mr-2" style="transform: scaleX(-1)" size="small">mdi-key-variant</v-icon>
            </template>
            <template #append>
              <v-icon v-if="isMembersLoading" class="ml-2" style="transform: scaleX(-1)" size="small">
                mdi-spin mdi-loading
              </v-icon>
            </template>
          </v-chip>
        </v-col>
        <v-col
          v-if="!isGroupStudentPasswordEditableCheck.allowed"
          cols="auto"
          class="d-flex align-center justify-end pl-0"
        >
          <PermissionLock :reason="isGroupStudentPasswordEditableCheck.reason" />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>
